/**
 * Security Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const SECURITY_ARTICLES_ES: HelpArticle[] = [
  {
    id: "sec-001",
    slug: "how-we-protect-your-funds",
    title: "Cómo protegemos tus fondos",
    description: "Medidas de seguridad para mantener tus activos seguros",
    category: "security",
    popular: true,
    order: 1,
    keywords: ["protección", "seguridad", "custodia", "seguro", "activos"],
    content: `
# Cómo protegemos tus fondos

Tu seguridad es nuestra máxima prioridad. Así es como mantenemos tus fondos seguros.

## Seguridad de activos

### Criptomonedas
- Socios de custodia líderes en la industria
- Wallets multi-firma
- Almacenamiento en frío para reservas
- Auditorías de seguridad regulares

### Moneda fiat
- Depositada en bancos asegurados por FDIC
- Cuentas de clientes segregadas
- Sin mezcla de fondos
- Reconciliación diaria

## Seguridad de plataforma

### Infraestructura
- Certificación SOC 2 Tipo II
- Alojado en AWS con encriptación
- SLA de 99.9% de disponibilidad
- Monitoreo 24/7

### Seguridad de aplicación
- Encriptación de extremo a extremo
- Pruebas de penetración regulares
- Programa de recompensas por bugs
- Auditorías de código

## Seguridad de cuenta

### Autenticación
- Sin contraseña (enlaces mágicos)
- Autenticación de dos factores
- Verificación de dispositivo
- Gestión de sesiones

### Control de acceso
- Lista blanca de IP (opcional)
- Lista blanca de direcciones de retiro
- Períodos de espera para cambios
- Notificaciones de actividad

## Cumplimiento

- Licencia de transmisor de dinero (donde se requiere)
- Cumplimiento AML/KYC
- Verificación de sanciones
- Monitoreo de transacciones
- Reportes regulatorios

## Seguros

- Cobertura de seguro contra crimen
- Seguro cibernético
- Seguro E&O
- Detalles de cobertura disponibles bajo solicitud

## Lo que NO hacemos

Almacenar contraseñas (sin contraseña)
Guardar tus claves privadas
Compartir datos sin consentimiento
Procesar transacciones sospechosas
    `.trim(),
  },
  {
    id: "sec-002",
    slug: "recognizing-phishing",
    title: "Reconocer phishing",
    description: "Protégete de ataques de phishing",
    category: "security",
    order: 2,
    keywords: ["phishing", "estafa", "fraude", "falso", "ataque"],
    content: `
# Reconocer phishing

Aprende a identificar y evitar intentos de phishing.

## ¿Qué es el phishing?

El phishing es cuando los atacantes intentan engañarte para:
- Revelar credenciales de acceso
- Enviar dinero a direcciones falsas
- Instalar software malicioso
- Proporcionar información personal

## Métodos comunes de phishing

### Correos falsos
- Parecen ser de Supabank
- Lenguaje urgente
- Enlaces sospechosos
- Solicitan información sensible

### Sitios web falsos
- Nombres de dominio similares
- Diseño copiado
- Piden iniciar sesión
- Sin HTTPS

### Redes sociales
- Cuentas de soporte falsas
- Mensajes directos pidiendo información
- Sorteos falsos
- Suplantación de identidad

### Llamadas telefónicas
- Dicen ser de Supabank
- Solicitan códigos de verificación
- Presionan para actuar inmediatamente
- Amenazan con cierre de cuenta

## Cómo identificar phishing

### Verificar dirección de correo
- Oficial: @supabank.xyz
- Sospechoso: @supabank-support.com

### Verificar enlaces (antes de hacer clic)
- Pasa el cursor para ver el destino
- Oficial: supabank.xyz
- Sospechoso: supabank.xyz.fake.com

### Señales de alerta
⚠️ Lenguaje urgente/amenazante
⚠️ Errores de ortografía/gramática
⚠️ Solicitan contraseñas
⚠️ Archivos adjuntos inesperados
⚠️ Presión para "actuar ahora"

## Lo que NUNCA te pediremos

Supabank NUNCA:
- Pedirá tu contraseña
- Solicitará códigos 2FA por correo
- Te pedirá enviar cripto "para verificación"
- Amenazará con cierre de cuenta por falta de pago
- Te contactará en redes sociales para soporte

## Si sospechas de phishing

1. No hagas clic en ningún enlace
2. No respondas ni proporciones información
3. Reporta a security@supabank.xyz
4. Reenvía el correo sospechoso
5. Elimina el mensaje
    `.trim(),
  },
  {
    id: "sec-003",
    slug: "reporting-suspicious-activity",
    title: "Reportar actividad sospechosa",
    description: "Cómo reportar preocupaciones de seguridad",
    category: "security",
    order: 3,
    keywords: ["reportar", "sospechoso", "fraude", "alerta", "notificar"],
    content: `
# Reportar actividad sospechosa

¿Ves algo preocupante? Aquí te explicamos cómo reportarlo.

## Qué reportar

### En tu cuenta
- Transacciones que no hiciste
- Inicio de sesión desde ubicación desconocida
- Ajustes cambiados que no modificaste
- Fondos faltantes

### Phishing/Estafas
- Correos sospechosos
- Sitios web falsos
- Suplantación en redes sociales
- Estafas telefónicas

### Vulnerabilidades de seguridad
- Errores del sitio web
- Problemas de la aplicación
- Debilidades de seguridad
- Exposición de datos

## Cómo reportar

### Preocupaciones de cuenta
**Urgente** (acceso no autorizado):
1. Correo: security@supabank.xyz
2. Incluye: Correo de cuenta, descripción
3. Respondemos en 1 hora

**No urgente**:
1. Correo: support@supabank.xyz
2. O usa el soporte en la app
3. Respuesta en 24 horas

### Reportes de phishing
1. Correo: security@supabank.xyz
2. Asunto: "Reporte de phishing"
3. Incluye: Captura de pantalla o correo reenviado
4. Investigamos inmediatamente

### Vulnerabilidades de seguridad
Programa de recompensas por bugs:
1. Correo: security@supabank.xyz
2. Describe la vulnerabilidad
3. Incluye pasos para reproducir
4. Recompensas disponibles para reportes válidos

## Qué pasa después de reportar

1. **Acuse de recibo inmediato**
2. **Comienza la investigación**
3. **Cuenta asegurada si es necesario**
4. **Actualizaciones regulares proporcionadas**
5. **Resolución y explicación**

## Proteger tu cuenta

Mientras investigamos:
- Cambia la contraseña del correo
- Activa/restablece 2FA
- Revisa transacciones recientes
- Verifica dispositivos conectados
    `.trim(),
  },
  {
    id: "sec-004",
    slug: "lost-device-procedure",
    title: "Procedimiento por dispositivo perdido",
    description: "Qué hacer si pierdes acceso a tu dispositivo",
    category: "security",
    order: 4,
    keywords: ["perdido", "dispositivo", "teléfono", "robado", "acceso"],
    content: `
# Procedimiento por dispositivo perdido

¿Perdiste tu teléfono o computadora? Esto es lo que debes hacer inmediatamente.

## Pasos inmediatos

### 1. Asegura tu correo
- Cambia la contraseña del correo
- Activa 2FA en el correo
- Revisa los registros de acceso al correo

### 2. Revoca sesiones de Supabank
En otro dispositivo:
1. Inicia sesión en supabank.xyz
2. Ve a Ajustes > Seguridad
3. Haz clic en "Cerrar todas las sesiones"
4. Confirma con 2FA

### 3. Revisa la actividad de la cuenta
- Verifica transacciones recientes
- Busca actividad no autorizada
- Revisa el historial de inicios de sesión

## Si perdiste el dispositivo de 2FA

### Con códigos de respaldo
1. Ve a la página de inicio de sesión
2. Introduce tu correo para el enlace mágico
3. En el aviso de 2FA, haz clic en "Usar código de respaldo"
4. Introduce el código de respaldo
5. Inicia sesión y restablece 2FA

### Sin códigos de respaldo
1. Envía correo a support@supabank.xyz
2. Asunto: "Recuperación 2FA - Dispositivo perdido"
3. Incluye:
   - Correo de la cuenta
   - Nombre completo
   - Fecha de nacimiento
   - Detalles de transacciones recientes
4. Completa la verificación de identidad
5. Se restablecerá el 2FA

## Consejos de prevención

### Antes de que suceda
Guarda los códigos de respaldo fuera de línea
Usa app autenticadora con respaldo en la nube
Mantén tu correo muy seguro
Conoce los detalles de tu cuenta

### Mantenimiento regular
Revisa las sesiones activas mensualmente
Actualiza la información de recuperación
Comprueba que los códigos de respaldo funcionen
Mantén la información de contacto actualizada

## Plazos

| Acción | Tiempo |
|--------|--------|
| Revocación de sesión | Inmediato |
| Revisión de seguridad del correo | Inmediato |
| Recuperación 2FA (con códigos) | 5 minutos |
| Recuperación 2FA (soporte) | 24-48 horas |
    `.trim(),
  },
  {
    id: "sec-005",
    slug: "api-key-security",
    title: "Seguridad de claves API",
    description: "Mejores prácticas para la gestión de claves API",
    category: "security",
    order: 5,
    keywords: ["api", "clave", "integración", "desarrollador", "secreto"],
    content: `
# Seguridad de claves API

Protege tus claves API e integraciones.

## Fundamentos de claves API

### Qué hacen las claves API
- Autentican solicitudes a la API
- Identifican tu aplicación
- Rastrean uso y límites
- Controlan permisos de acceso

### Tipos de claves
- **Solo lectura**: Solo ver datos
- **Acceso completo**: Lectura y escritura
- **Limitado**: Solo acciones específicas

## Mejores prácticas de seguridad

### Almacenamiento
Usa variables de entorno
Usa gestores de secretos (AWS Secrets, Vault)
Nunca hagas commit al control de versiones
Encripta en reposo

### Acceso
Limita quién tiene acceso
Usa el principio de mínimo privilegio
Audita el acceso regularmente
Rota las claves periódicamente

### Uso
Usa solo HTTPS
Implementa límite de tasa
Monitorea anomalías
Registra las llamadas a API

## Lo que NO debes hacer

Codificar claves en el código fuente
Compartir claves por correo/chat
Usar claves de producción en desarrollo
Almacenar en repositorios públicos
Compartir claves entre aplicaciones

## Rotación de claves

### Cuándo rotar
- Programado (cada 90 días)
- Después de que un empleado se va
- Después de un incidente de seguridad
- Cuando la clave pueda estar comprometida

### Cómo rotar
1. Genera nueva clave en Ajustes
2. Actualiza tu aplicación
3. Prueba exhaustivamente
4. Desactiva la clave antigua
5. Monitorea por problemas

## Si la clave está comprometida

1. **Revoca la clave inmediatamente**
2. Genera nueva clave
3. Actualiza la aplicación
4. Audita los registros de API
5. Reporta a security@supabank.xyz

## Monitoreo

- Revisa los registros de API regularmente
- Configura alertas de uso
- Observa patrones inusuales
- Monitorea tasas de error
    `.trim(),
  },
  {
    id: "sec-006",
    slug: "compliance-aml",
    title: "Cumplimiento y AML",
    description: "Nuestras prácticas de cumplimiento normativo",
    category: "security",
    order: 6,
    keywords: ["cumplimiento", "aml", "regulaciones", "legal", "kyc"],
    content: `
# Cumplimiento y AML

Cómo mantenemos el cumplimiento normativo y prevenimos el crimen financiero.

## Marco regulatorio

### Licencias
- Licencias de transmisor de dinero (estados de EE.UU.)
- Cumplimiento europeo (pendiente)
- Auditorías y exámenes regulares

### Estándares
- Cumplimiento BSA/AML
- Verificación de sanciones OFAC
- Reportes a FinCEN
- Requisitos específicos por estado

## Conoce a tu cliente (KYC)

### Por qué verificamos identidad
- Requisito regulatorio
- Prevención de fraude
- Seguridad de cuenta
- Prevención de crimen financiero

### Qué verificamos
- Documento de identidad oficial
- Coincidencia con selfie
- Dirección (niveles avanzados)
- Origen de fondos (alto volumen)

## Anti-Lavado de Dinero (AML)

### Monitoreo de transacciones
- Verificación en tiempo real
- Análisis de patrones
- Puntuación de riesgo
- Detección de actividad sospechosa

### Qué verificamos
- Listas de sanciones (OFAC, ONU, UE)
- Personas políticamente expuestas (PEPs)
- Medios adversos
- Jurisdicciones de alto riesgo

## Tus responsabilidades

### Divulgaciones requeridas
- Información personal precisa
- Origen de fondos (si se solicita)
- Propósito empresarial (cuentas empresariales)
- Titularidad beneficiaria

### Actividades prohibidas
Lavado de dinero
Financiamiento del terrorismo
Evasión de sanciones
Evasión fiscal
Fraude

## Privacidad y datos

### Qué recopilamos
- Documentos de identidad
- Registros de transacciones
- Registros de comunicación
- Información del dispositivo

### Cómo lo protegemos
- Almacenamiento encriptado
- Acceso limitado
- Políticas de retención
- Derecho a eliminación (donde está permitido)

## ¿Preguntas?

Contacta con compliance@supabank.xyz para consultas relacionadas con cumplimiento.
    `.trim(),
  },
];
